// Builds the "Copy for LLMs" payloads for API reference pages. An endpoint
// section is a `##` heading whose first line of body is an endpoint, e.g.
//   ## List folders
//   `GET https://www.instapaper.com/api/2/folders`
// Each copy is that section's markdown (through the next `##`), plus every
// "<Name> object" section on the page so the response shape comes along, under
// a short header and with links made absolute so the text stands on its own.

const DOCS_ORIGIN = 'https://www.instapaper.com';

const ENDPOINT_LINE = /^`(GET|POST|PUT|PATCH|DELETE) \S+`/;
const OBJECT_HEADING = /^[A-Z][\w ]* object$/;
const H2_LINE = /^##\s+(.+)$/;
const FENCE_LINE = /^(```|~~~)/;
const CALLOUT_OPEN = /^:::(\w+)(?:\{title="([^"]*)"\})?\s*$/;
const CALLOUT_CLOSE = /^:::\s*$/;

// Per-version auth and error conventions for the copy header, since those live
// on other pages. Versions without an entry just get the source link.
const VERSION_HINTS: Record<string, string[]> = {
  v2: [
    `Authentication: send \`Authorization: Bearer $ACCESS_TOKEN\` with every request. See ${DOCS_ORIGIN}/developers/overview/authentication.`,
    `Errors: failures use the HTTP status code, with a body of \`{"error": {"code": <status>, "message": "…"}}\`. See ${DOCS_ORIGIN}/developers/v2/reference/errors.`,
  ],
};

export interface EndpointCopyContext {
  pageTitle: string;
  /** Site path of the page, e.g. `/developers/v2/reference/folders`. */
  pagePath: string;
  version?: string;
}

interface MarkdownSection {
  heading: string;
  /** 1-based source line of the `##` heading. */
  line: number;
  markdown: string;
}

/** Split markdown into `##` sections, ignoring `##` lines inside code fences. */
function splitH2Sections(markdown: string): MarkdownSection[] {
  const lines = markdown.split('\n');
  const starts: { heading: string; index: number }[] = [];
  let inFence = false;

  lines.forEach((line, index) => {
    if (FENCE_LINE.test(line)) {
      inFence = !inFence;
      return;
    }
    const match = !inFence && line.match(H2_LINE);
    if (match) starts.push({ heading: match[1].trim(), index });
  });

  return starts.map(({ heading, index }, i) => ({
    heading,
    line: index + 1,
    markdown: lines
      .slice(index, starts[i + 1]?.index ?? lines.length)
      .join('\n')
      .trim(),
  }));
}

function isEndpointSection(section: MarkdownSection): boolean {
  const body = section.markdown.split('\n').slice(1);
  const firstLine = body.find((line) => line.trim() !== '');
  return firstLine != null && ENDPOINT_LINE.test(firstLine);
}

/** Rewrite `:::note{title="…"}` callout directives as plain blockquotes. */
function flattenCallouts(markdown: string): string {
  const out: string[] = [];
  let inCallout = false;
  let label = '';

  for (const line of markdown.split('\n')) {
    const open = !inCallout && line.match(CALLOUT_OPEN);
    if (open) {
      inCallout = true;
      label = open[2] || open[1].charAt(0).toUpperCase() + open[1].slice(1);
      continue;
    }
    if (inCallout && CALLOUT_CLOSE.test(line)) {
      inCallout = false;
      continue;
    }
    if (!inCallout) {
      out.push(line);
    } else if (label) {
      out.push(`> **${label}:** ${line}`);
      label = '';
    } else {
      out.push(line.trim() === '' ? '>' : `> ${line}`);
    }
  }

  return out.join('\n');
}

/** Make site-relative and in-page links absolute. */
function absolutizeLinks(markdown: string, pagePath: string): string {
  return markdown
    .replace(/\]\(\//g, `](${DOCS_ORIGIN}/`)
    .replace(/\]\(#/g, `](${DOCS_ORIGIN}${pagePath}#`);
}

function copyHeader({ pageTitle, pagePath, version }: EndpointCopyContext): string {
  const api = version ? `Instapaper API ${version}` : 'Instapaper API';
  const lines = [`# ${api}: ${pageTitle}`, `Source: ${DOCS_ORIGIN}${pagePath}`];
  if (version) lines.push(...(VERSION_HINTS[version] ?? []));
  return lines.join('\n\n');
}

export interface EndpointSectionCopy {
  heading: string;
  markdown: string;
}

/**
 * Map of `##` heading source line -> LLM-ready copy, for every endpoint section
 * on the page. Headings that aren't endpoints have no entry.
 */
export function getEndpointSectionCopies(
  markdown: string,
  context: EndpointCopyContext,
): Map<number, EndpointSectionCopy> {
  const sections = splitH2Sections(markdown);
  const objects = sections.filter((s) => OBJECT_HEADING.test(s.heading));
  const header = copyHeader(context);
  const copies = new Map<number, EndpointSectionCopy>();

  for (const section of sections) {
    if (!isEndpointSection(section)) continue;
    const body = [section, ...objects].map((s) => s.markdown).join('\n\n');
    copies.set(section.line, {
      heading: section.heading,
      markdown: `${header}\n\n${absolutizeLinks(flattenCallouts(body), context.pagePath)}\n`,
    });
  }

  return copies;
}
